import { useEffect, useMemo, useRef, useState } from 'react';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { selectSettings } from 'ducks/settings/selectors';
import { Icon } from 'framework/Icon';
import useModalTheme from 'framework/modal/hooks/useModalTheme';
import { colors } from 'framework/theme/constants/colors';
import { useCaptureClick } from 'hooks/useCaptureClick';
import { useAppSelector } from 'store/hooks';

import { EMPTY_FUNCTION } from '../../constants';

import { OverlayContext, useOverlayContext } from './context/OverlayContext';
import { OverlayElement } from './elements';
import { OverlayBodyProps, OverlayFooterProps, OverlayHeaderProps, OverlayProps } from './types';

import cn from './Overlay.module.scss';

export const Overlay = ({
  is_open = false,
  duration = 200,
  width = '325px',
  onClose = EMPTY_FUNCTION,
  theme,
  children,
}: OverlayProps) => {
  const { offset_navigation } = useAppSelector(selectSettings);
  const ref = useRef<HTMLDivElement | null>(null);
  const [is_visible, setVisibile] = useState(is_open);
  useCaptureClick({ refs: [ref], onClickOutside: () => onClose() });
  const modal_theme = useModalTheme(theme);

  useEffect(() => {
    setVisibile(is_open);
  }, [is_open]);

  const context = useMemo(() => ({ theme, is_open, onClose }), [theme, is_open, onClose]);

  return (
    <OverlayContext.Provider value={context}>
      <OverlayElement
        ref={ref}
        className={cn.overlay}
        width={width}
        offset={`${offset_navigation - 1}px`}
        duration={duration}
        is_visible={is_visible}
        theme={modal_theme}
      >
        {children}
      </OverlayElement>
    </OverlayContext.Provider>
  );
};

const Header = ({ children, dismiss = false }: OverlayHeaderProps) => {
  const { onClose } = useOverlayContext();
  return (
    <>
      <div className={cn.header} onClick={onClose}>
        <h2>{children}</h2>
        <div className={cn.flex} />
        {dismiss && <Icon icon={faClose} size="14px" width="30px" button />}
      </div>
      <div className={cn.separator} />
    </>
  );
};

const Body = ({ children, gap = '0px', padding = '0px 12px' }: OverlayBodyProps) => {
  return (
    <div className={cn.body} style={{ gap, padding, borderTop: colors.dark[9] }}>
      {children}
    </div>
  );
};

Overlay.Footer = ({ children, margin = '0px 0px 0px 0px' }: OverlayFooterProps) => {
  return (
    <div className={cn.footer} style={{ margin }}>
      {children}
    </div>
  );
};

Overlay.Header = Header;
Overlay.Body = Body;
